/**
 * Token burn transaction logic
 * Handles building, sending, and confirming burn transactions
 */

import { Connection, PublicKey, Transaction, TransactionSignature, ComputeBudgetProgram, SystemProgram, LAMPORTS_PER_SOL, TransactionInstruction } from "@solana/web3.js";
import {
  createBurnCheckedInstruction,
  createCloseAccountInstruction,
  getMint,
  getAccount,
  TOKEN_2022_PROGRAM_ID,
  TOKEN_PROGRAM_ID,
  unpackAccount,
  getTransferFeeAmount,
  createHarvestWithheldTokensToMintInstruction,
} from "@solana/spl-token";
import { getUserFriendlyError, mightHaveSucceeded } from "./errors";
import { parseTokenAmount } from "./format";
import { createUmi } from "@metaplex-foundation/umi-bundle-defaults";
import { publicKey as umiPublicKey, createNoopSigner } from "@metaplex-foundation/umi";
import { burnV1, mplTokenMetadata, TokenStandard, findMetadataPda } from "@metaplex-foundation/mpl-token-metadata";
import { toWeb3JsInstruction } from "@metaplex-foundation/umi-web3js-adapters";
import type { NFTInfo } from "./nfts";

/** Platform fee configuration */
export const FORGEPAD_FEE_PERCENT = 7;
export const FORGEPAD_FEE_WALLET = "4uGpUzreksHAR5mtS6dHEUyKpRANxCsDQBSTHQsnYhut";
export const TYPICAL_RENT_PER_ACCOUNT = 0.00203928; // SOL, per token account
export const TYPICAL_RENT_LAMPORTS = Math.floor(TYPICAL_RENT_PER_ACCOUNT * LAMPORTS_PER_SOL);

/** NFT rent constants - total for all accounts (token + metadata + master edition) */
export const TYPICAL_NFT_RENT = 0.0103; // SOL, approximately
export const TYPICAL_NFT_RENT_LAMPORTS = Math.floor(TYPICAL_NFT_RENT * LAMPORTS_PER_SOL);

/** Max NFTs per batch transaction (heavier instructions than token closes) */
export const MAX_NFTS_PER_TX = 5;

/**
 * Status callback for burn progress updates
 */
export type BurnStatusCallback = (status: BurnStatus) => void;

/**
 * Burn operation status
 */
export interface BurnStatus {
  stage: "preparing" | "signing" | "confirming" | "checking" | "success" | "error";
  message: string;
  signature?: string;
  error?: string;
}

/**
 * Burn operation result
 */
export interface BurnResult {
  success: boolean;
  signature?: string;
  error?: string;
  /** Whether the token account was closed (rent reclaimed) */
  accountClosed?: boolean;
}

/**
 * Check the status of a transaction signature
 * @param connection - Solana RPC connection
 * @param signature - Transaction signature to check
 * @returns 'confirmed' | 'finalized' | 'failed' | 'not_found'
 */
export async function checkSignatureStatus(
  connection: Connection,
  signature: string
): Promise<"confirmed" | "finalized" | "failed" | "not_found"> {
  try {
    const status = await connection.getSignatureStatus(signature, {
      searchTransactionHistory: true,
    });

    if (!status.value) {
      return "not_found";
    }

    if (status.value.err) {
      return "failed";
    }

    const confirmationStatus = status.value.confirmationStatus;
    if (confirmationStatus === "confirmed" || confirmationStatus === "finalized") {
      return confirmationStatus;
    }

    return "not_found";
  } catch {
    return "not_found";
  }
}

/**
 * Wait for transaction confirmation with timeout and status polling
 * @param connection - Solana RPC connection
 * @param signature - Transaction signature
 * @param timeoutMs - Timeout in milliseconds (default 90s)
 * @param onStatus - Optional status callback
 * @returns Confirmation result
 */
export async function waitForConfirmation(
  connection: Connection,
  signature: string,
  timeoutMs: number = 90000,
  onStatus?: (message: string) => void
): Promise<{ success: boolean; status: string }> {
  const startTime = Date.now();

  onStatus?.("Waiting for confirmation...");

  // Polling loop
  while (Date.now() - startTime < timeoutMs) {
    const status = await checkSignatureStatus(connection, signature);

    if (status === "confirmed" || status === "finalized") {
      return { success: true, status };
    }

    if (status === "failed") {
      return { success: false, status: "Transaction failed on-chain" };
    }

    // Wait before next poll
    await new Promise((resolve) => setTimeout(resolve, 2000));
    const elapsed = Math.floor((Date.now() - startTime) / 1000);
    onStatus?.(`Confirming on-chain... (${elapsed}s)`);
  }

  // Timeout reached - do final checks
  onStatus?.("Checking final status...");

  // Multiple final checks with delays
  for (let i = 0; i < 3; i++) {
    const finalStatus = await checkSignatureStatus(connection, signature);

    if (finalStatus === "confirmed" || finalStatus === "finalized") {
      return { success: true, status: finalStatus };
    }

    if (finalStatus === "failed") {
      return { success: false, status: "Transaction failed on-chain" };
    }

    if (i < 2) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }
  }

  return {
    success: false,
    status: `Confirmation timed out. Check Solscan for status.`,
  };
}

/**
 * Build a burn transaction
 * @param connection - Solana RPC connection
 * @param tokenAccount - Token account public key
 * @param mint - Token mint public key
 * @param owner - Owner wallet public key
 * @param amount - Human-readable amount to burn
 * @param decimals - Token decimals
 * @param programId - Token program ID
 * @param closeAccount - Whether to close the token account after burn (reclaims rent)
 * @returns Built transaction ready for signing
 */
export async function buildBurnTransaction(
  connection: Connection,
  tokenAccount: PublicKey,
  mint: PublicKey,
  owner: PublicKey,
  amount: string,
  decimals: number,
  programId: PublicKey,
  closeAccount: boolean = false
): Promise<Transaction> {
  // Convert human amount to raw
  const rawAmount = parseTokenAmount(amount, decimals);

  // Verify mint info
  const mintInfo = await getMint(connection, mint, "confirmed", programId);

  // Build transaction with compute budget instructions first
  // This prevents Phantom simulation failures and "malicious app" warnings
  const transaction = new Transaction();

  // Add compute budget instructions (required for reliable simulation)
  const computeLimitIx = ComputeBudgetProgram.setComputeUnitLimit({
    units: 100000, // 100k units - plenty for burn + optional close
  });
  const computePriceIx = ComputeBudgetProgram.setComputeUnitPrice({
    microLamports: 50000, // Priority fee for faster inclusion
  });
  transaction.add(computeLimitIx);
  transaction.add(computePriceIx);

  // Create burn instruction using CHECKED version (required for safety)
  const burnInstruction = createBurnCheckedInstruction(
    tokenAccount,
    mint,
    owner,
    rawAmount,
    mintInfo.decimals,
    [],
    programId
  );
  transaction.add(burnInstruction);

  // If closing the account (burning 100%), add close instruction to reclaim rent
  // The close instruction refunds the rent (~0.002 SOL) to the owner
  // Note: Most Token-2022 accounts can be closed just like classic SPL.
  // Some Token-2022 tokens have extensions that prevent closure, but those will
  // fail gracefully during simulation - the burn itself will still succeed.
  if (closeAccount) {
    const closeInstruction = createCloseAccountInstruction(
      tokenAccount,  // Account to close
      owner,         // Destination for rent refund
      owner,         // Authority (owner)
      [],            // Multi-sig signers (none)
      programId      // Use the token's program ID (SPL Token or Token-2022)
    );
    transaction.add(closeInstruction);

    // Add platform fee transfer (7% of rent reclaimed)
    // Fee is taken from the rent refund the user receives
    const feeAmount = Math.floor(TYPICAL_RENT_LAMPORTS * FORGEPAD_FEE_PERCENT / 100);
    if (feeAmount > 0) {
      const feeInstruction = SystemProgram.transfer({
        fromPubkey: owner,
        toPubkey: new PublicKey(FORGEPAD_FEE_WALLET),
        lamports: feeAmount,
      });
      transaction.add(feeInstruction);
      console.log(`[burn] Added platform fee transfer: ${feeAmount} lamports (${FORGEPAD_FEE_PERCENT}% of rent)`);
    }
  }

  // Get fresh blockhash
  const { blockhash } = await connection.getLatestBlockhash("confirmed");
  transaction.recentBlockhash = blockhash;
  transaction.feePayer = owner;

  return transaction;
}

/**
 * Execute a token burn operation
 * This is the main entry point for burning tokens
 *
 * @param connection - Solana RPC connection
 * @param tokenAccount - Token account to burn from
 * @param mint - Token mint address
 * @param owner - Owner wallet public key
 * @param amount - Human-readable amount to burn
 * @param decimals - Token decimals
 * @param programId - Token program ID (SPL Token or Token-2022)
 * @param sendTransaction - Function to send the transaction (from wallet adapter)
 * @param onStatus - Optional status callback for progress updates
 * @param closeAccount - Whether to close the token account after burn (for 100% burns)
 * @returns Burn result with success status and signature
 */
export async function executeBurn(
  connection: Connection,
  tokenAccount: PublicKey,
  mint: PublicKey,
  owner: PublicKey,
  amount: string,
  decimals: number,
  programId: PublicKey,
  sendTransaction: (tx: Transaction) => Promise<TransactionSignature>,
  onStatus?: BurnStatusCallback,
  closeAccount: boolean = false
): Promise<BurnResult> {
  let signature: string | null = null;

  // For full burns, we'll attempt to close the account to reclaim rent
  // Most Token-2022 accounts can be closed; those with blocking extensions will
  // fail gracefully during simulation
  const willCloseAccount = closeAccount;

  try {
    // Stage 1: Prepare transaction
    onStatus?.({ stage: "preparing", message: "Building transaction..." });

    const transaction = await buildBurnTransaction(
      connection,
      tokenAccount,
      mint,
      owner,
      amount,
      decimals,
      programId,
      closeAccount
    );

    // Pre-simulate transaction to catch errors before user signs
    const simulation = await connection.simulateTransaction(transaction);

    if (simulation.value.err) {
      console.error("[burn] Simulation failed:", simulation.value.err);
      console.error("[burn] Simulation logs:", simulation.value.logs);
      throw new Error(`Transaction simulation failed: ${JSON.stringify(simulation.value.err)}`);
    }

    console.log("[burn] Simulation passed, sending transaction");

    // Stage 2: Request signature
    onStatus?.({ stage: "signing", message: "Approve in your wallet..." });

    signature = await sendTransaction(transaction);

    console.log("[burn] Transaction sent:", signature, willCloseAccount ? "(with account close)" : "(burn only)");

    // Stage 3: Wait for confirmation
    onStatus?.({
      stage: "confirming",
      message: "Confirming on-chain...",
      signature,
    });

    const result = await waitForConfirmation(connection, signature, 90000, (msg) =>
      onStatus?.({ stage: "confirming", message: msg, signature: signature! })
    );

    if (result.success) {
      const successMsg = willCloseAccount ? "Burn successful! Rent reclaimed." : "Burn successful!";
      onStatus?.({
        stage: "success",
        message: successMsg,
        signature,
      });
      return { success: true, signature, accountClosed: willCloseAccount };
    } else {
      onStatus?.({
        stage: "error",
        message: result.status,
        signature,
        error: result.status,
      });
      return { success: false, signature, error: result.status };
    }
  } catch (error) {
    // Log raw error for debugging (before converting to user-friendly message)
    console.error("[burn error] Raw error:", {
      message: error instanceof Error ? error.message : String(error),
      name: error instanceof Error ? error.name : "Unknown",
      stack: error instanceof Error ? error.stack : undefined,
      signature: signature || "none",
      closeAccount,
    });

    // If we have a signature, check if it actually succeeded
    if (signature && mightHaveSucceeded(error)) {
      onStatus?.({ stage: "checking", message: "Verifying transaction status...", signature });

      // Give it a moment then check
      await new Promise((resolve) => setTimeout(resolve, 3000));

      const status = await checkSignatureStatus(connection, signature);
      if (status === "confirmed" || status === "finalized") {
        const successMsg = willCloseAccount ? "Burn successful! Rent reclaimed." : "Burn successful!";
        onStatus?.({
          stage: "success",
          message: successMsg,
          signature,
        });
        return { success: true, signature, accountClosed: willCloseAccount };
      }
    }

    const friendlyError = getUserFriendlyError(error);
    console.error("[burn error] User-friendly:", friendlyError);

    onStatus?.({
      stage: "error",
      message: friendlyError,
      signature: signature || undefined,
      error: friendlyError,
    });

    return {
      success: false,
      signature: signature || undefined,
      error: friendlyError,
    };
  }
}

/**
 * Input account for close operations - includes mint for Token-2022 harvest checks
 */
export interface CloseAccountInput {
  pubkey: PublicKey;
  programId: PublicKey;
  mint: PublicKey;
}

/**
 * Pre-computed withheld fee info for Token-2022 accounts
 * Map: account pubkey base58 -> { mint, withheldAmount }
 */
export type WithheldFeeInfo = Map<string, { mint: PublicKey; withheldAmount: bigint }>;

/**
 * Batch-fetch withheld transfer fee info for Token-2022 accounts
 * Call ONCE for all accounts before building transactions, not per-batch.
 * Uses getMultipleAccountsInfo in chunks of 100 (RPC limit).
 *
 * @param connection - Solana RPC connection
 * @param accounts - All accounts to check (filters to Token-2022 internally)
 * @returns Map of account pubkey -> withheld fee info
 */
export async function fetchWithheldFeeInfo(
  connection: Connection,
  accounts: CloseAccountInput[]
): Promise<WithheldFeeInfo> {
  const result: WithheldFeeInfo = new Map();

  // Filter to Token-2022 accounts only
  const token2022Accounts = accounts.filter(a => a.programId.equals(TOKEN_2022_PROGRAM_ID));
  if (token2022Accounts.length === 0) {
    return result;
  }

  // Fetch in chunks of 100 (RPC limit for getMultipleAccountsInfo)
  const CHUNK_SIZE = 100;
  for (let i = 0; i < token2022Accounts.length; i += CHUNK_SIZE) {
    const chunk = token2022Accounts.slice(i, i + CHUNK_SIZE);
    const accountInfos = await connection.getMultipleAccountsInfo(
      chunk.map(a => a.pubkey)
    );

    for (let j = 0; j < chunk.length; j++) {
      const info = accountInfos[j];
      if (!info) continue;

      try {
        const account = unpackAccount(chunk[j].pubkey, info, TOKEN_2022_PROGRAM_ID);
        const transferFeeAmount = getTransferFeeAmount(account);

        if (transferFeeAmount && transferFeeAmount.withheldAmount > 0n) {
          result.set(chunk[j].pubkey.toBase58(), {
            mint: chunk[j].mint,
            withheldAmount: transferFeeAmount.withheldAmount,
          });
          console.log(`[burn] Account ${chunk[j].pubkey.toBase58().slice(0, 8)}... has withheld fees: ${transferFeeAmount.withheldAmount}`);
        }
      } catch (e) {
        // Account may not have transfer fee extension - that's OK
      }
    }
  }

  return result;
}

/**
 * Result of building a close accounts transaction
 */
export interface BuildCloseAccountsTxResult {
  success: boolean;
  transaction?: Transaction;
  accountCount: number;
  rentLamports: number;
  feeLamports: number;
  accounts: CloseAccountInput[];
  error?: string;
}

/**
 * Build a transaction to close empty token accounts (without sending)
 * Used for batch signing with signAllTransactions
 *
 * FAST PATH: No RPC calls. Builds synchronously from pre-fetched data.
 * Caller provides blockhash (fetch ONCE for all transactions, right before signing).
 * Caller provides withheldFeeInfo (fetch ONCE via fetchWithheldFeeInfo for all accounts).
 *
 * For Token-2022 accounts with withheld transfer fees, prepends harvest instructions
 * to move those fees to the mint before closing (required for closeAccount to succeed).
 *
 * @param emptyAccounts - Array of empty token accounts to close (max ~10, reduced if harvests needed)
 * @param owner - Owner wallet public key
 * @param blockhash - Recent blockhash (caller fetches once for all txs)
 * @param withheldFeeInfo - Pre-fetched withheld fee info (caller fetches once via fetchWithheldFeeInfo)
 * @returns Built transaction ready for signing
 */
export function buildCloseAccountsTransaction(
  emptyAccounts: CloseAccountInput[],
  owner: PublicKey,
  blockhash: string,
  withheldFeeInfo: WithheldFeeInfo = new Map()
): BuildCloseAccountsTxResult {
  if (emptyAccounts.length === 0) {
    return { success: false, accountCount: 0, rentLamports: 0, feeLamports: 0, accounts: [], error: "No accounts provided" };
  }

  // Base limit: ~10 close instructions per tx
  // Reduced when harvests are present (each harvest adds instruction overhead)
  const MAX_ACCOUNTS_PER_TX = 10;
  const MAX_ACCOUNTS_WITH_HARVESTS = 7; // Conservative limit when harvests are present

  try {
    // Build harvest map from pre-fetched data: mint -> [source pubkeys]
    const harvestsByMint = new Map<string, PublicKey[]>();
    for (const account of emptyAccounts) {
      const feeInfo = withheldFeeInfo.get(account.pubkey.toBase58());
      if (feeInfo && feeInfo.withheldAmount > 0n) {
        const mintKey = feeInfo.mint.toBase58();
        if (!harvestsByMint.has(mintKey)) {
          harvestsByMint.set(mintKey, []);
        }
        harvestsByMint.get(mintKey)!.push(account.pubkey);
      }
    }

    // Determine batch size based on whether harvests are needed
    const needsHarvest = harvestsByMint.size > 0;
    const effectiveMaxAccounts = needsHarvest ? MAX_ACCOUNTS_WITH_HARVESTS : MAX_ACCOUNTS_PER_TX;
    const accountsToClose = emptyAccounts.slice(0, effectiveMaxAccounts);

    // Recalculate harvests for the actual accounts in this batch
    const batchHarvestsByMint = new Map<string, PublicKey[]>();
    for (const account of accountsToClose) {
      const feeInfo = withheldFeeInfo.get(account.pubkey.toBase58());
      if (feeInfo && feeInfo.withheldAmount > 0n) {
        const mintKey = feeInfo.mint.toBase58();
        if (!batchHarvestsByMint.has(mintKey)) {
          batchHarvestsByMint.set(mintKey, []);
        }
        batchHarvestsByMint.get(mintKey)!.push(account.pubkey);
      }
    }

    const transaction = new Transaction();

    // Add compute budget instructions (increase CU limit if harvesting)
    const baseUnitsPerAccount = 50000;
    const harvestUnitsPerMint = 30000;
    const totalUnits = (baseUnitsPerAccount * accountsToClose.length) + (harvestUnitsPerMint * batchHarvestsByMint.size);

    const computeLimitIx = ComputeBudgetProgram.setComputeUnitLimit({
      units: totalUnits,
    });
    const computePriceIx = ComputeBudgetProgram.setComputeUnitPrice({
      microLamports: 50000,
    });
    transaction.add(computeLimitIx);
    transaction.add(computePriceIx);

    // Add harvest instructions BEFORE close instructions (grouped by mint for efficiency)
    for (const [mintStr, sources] of batchHarvestsByMint) {
      const harvestIx = createHarvestWithheldTokensToMintInstruction(
        new PublicKey(mintStr),
        sources,
        TOKEN_2022_PROGRAM_ID
      );
      transaction.add(harvestIx);
      console.log(`[burn] Added harvest instruction for mint ${mintStr.slice(0, 8)}... with ${sources.length} source(s)`);
    }

    // Add close instructions for each account
    for (const account of accountsToClose) {
      const closeInstruction = createCloseAccountInstruction(
        account.pubkey,
        owner,
        owner,
        [],
        account.programId
      );
      transaction.add(closeInstruction);
    }

    // Calculate total rent and fee
    const totalRentLamports = accountsToClose.length * TYPICAL_RENT_LAMPORTS;
    const feeAmount = Math.floor(totalRentLamports * FORGEPAD_FEE_PERCENT / 100);

    // Add platform fee transfer
    if (feeAmount > 0) {
      const feeInstruction = SystemProgram.transfer({
        fromPubkey: owner,
        toPubkey: new PublicKey(FORGEPAD_FEE_WALLET),
        lamports: feeAmount,
      });
      transaction.add(feeInstruction);
    }

    // Apply blockhash (provided by caller, fetched once for all txs)
    transaction.recentBlockhash = blockhash;
    transaction.feePayer = owner;

    return {
      success: true,
      transaction,
      accountCount: accountsToClose.length,
      rentLamports: totalRentLamports,
      feeLamports: feeAmount,
      accounts: accountsToClose,
    };
  } catch (error) {
    console.error("[burn] Build close accounts tx error:", error);
    return {
      success: false,
      accountCount: 0,
      rentLamports: 0,
      feeLamports: 0,
      accounts: [],
      error: getUserFriendlyError(error),
    };
  }
}

/**
 * Result of closing empty accounts batch
 */
export interface CloseEmptyAccountsResult {
  success: boolean;
  signature?: string;
  accountsClosed: number;
  rentReclaimed: number; // in lamports
  feePaid: number; // in lamports
  error?: string;
}

/**
 * Close empty token accounts in a batch to reclaim rent
 * Processes up to 10 accounts per transaction (Solana limit)
 * Takes a 7% platform fee from total rent reclaimed
 *
 * For Token-2022 accounts with withheld transfer fees, prepends harvest instructions
 * to move those fees to the mint before closing (required for closeAccount to succeed).
 *
 * @param connection - Solana RPC connection
 * @param emptyAccounts - Array of empty token accounts to close
 * @param owner - Owner wallet public key
 * @param sendTransaction - Function to send the transaction
 * @param onStatus - Optional status callback
 * @returns Result with accounts closed and rent reclaimed
 */
export async function closeEmptyAccountsBatch(
  connection: Connection,
  emptyAccounts: CloseAccountInput[],
  owner: PublicKey,
  sendTransaction: (tx: Transaction) => Promise<string>,
  onStatus?: BurnStatusCallback
): Promise<CloseEmptyAccountsResult> {
  if (emptyAccounts.length === 0) {
    return { success: true, accountsClosed: 0, rentReclaimed: 0, feePaid: 0 };
  }

  // Base limit: ~10 close instructions per tx
  // Reduced when harvests are present (each harvest adds instruction overhead)
  const MAX_ACCOUNTS_PER_TX = 10;
  const MAX_ACCOUNTS_WITH_HARVESTS = 7;

  let signature: string | null = null;
  // Track accounts being closed (set inside try, used in catch for error recovery)
  let accountsToClose: CloseAccountInput[] = emptyAccounts.slice(0, MAX_ACCOUNTS_PER_TX);

  try {
    onStatus?.({ stage: "preparing", message: `Building transaction to close ${Math.min(emptyAccounts.length, MAX_ACCOUNTS_PER_TX)} accounts...` });

    // Separate Token-2022 accounts (may need harvest) from classic SPL accounts
    const token2022Accounts = emptyAccounts.filter(a => a.programId.equals(TOKEN_2022_PROGRAM_ID));

    // For Token-2022 accounts, batch-fetch to check for withheld transfer fees
    // Map: mint.toBase58() -> array of source account pubkeys that need harvest
    const harvestsByMint = new Map<string, PublicKey[]>();

    if (token2022Accounts.length > 0) {
      const accountInfos = await connection.getMultipleAccountsInfo(
        token2022Accounts.map(a => a.pubkey)
      );

      for (let i = 0; i < token2022Accounts.length; i++) {
        const info = accountInfos[i];
        if (!info) continue;

        try {
          const account = unpackAccount(token2022Accounts[i].pubkey, info, TOKEN_2022_PROGRAM_ID);
          const transferFeeAmount = getTransferFeeAmount(account);

          if (transferFeeAmount && transferFeeAmount.withheldAmount > 0n) {
            const mintKey = token2022Accounts[i].mint.toBase58();
            if (!harvestsByMint.has(mintKey)) {
              harvestsByMint.set(mintKey, []);
            }
            harvestsByMint.get(mintKey)!.push(token2022Accounts[i].pubkey);
            console.log(`[burn] Account ${token2022Accounts[i].pubkey.toBase58().slice(0, 8)}... has withheld fees: ${transferFeeAmount.withheldAmount}`);
          }
        } catch (e) {
          // Account may not have transfer fee extension - that's OK, proceed without harvest
          console.log(`[burn] Could not check transfer fee for ${token2022Accounts[i].pubkey.toBase58().slice(0, 8)}...:`, e);
        }
      }
    }

    // Determine batch size based on whether harvests are needed
    const needsHarvest = harvestsByMint.size > 0;
    const effectiveMaxAccounts = needsHarvest ? MAX_ACCOUNTS_WITH_HARVESTS : MAX_ACCOUNTS_PER_TX;
    accountsToClose = emptyAccounts.slice(0, effectiveMaxAccounts);

    // Recalculate harvests for the actual accounts in this batch
    const batchHarvestsByMint = new Map<string, PublicKey[]>();
    if (needsHarvest) {
      for (const account of accountsToClose) {
        if (!account.programId.equals(TOKEN_2022_PROGRAM_ID)) continue;
        const mintKey = account.mint.toBase58();
        const allSourcesForMint = harvestsByMint.get(mintKey);
        if (allSourcesForMint?.some(src => src.equals(account.pubkey))) {
          if (!batchHarvestsByMint.has(mintKey)) {
            batchHarvestsByMint.set(mintKey, []);
          }
          batchHarvestsByMint.get(mintKey)!.push(account.pubkey);
        }
      }
    }

    const transaction = new Transaction();

    // Add compute budget instructions (increase CU limit if harvesting)
    const baseUnitsPerAccount = 50000;
    const harvestUnitsPerMint = 30000;
    const totalUnits = (baseUnitsPerAccount * accountsToClose.length) + (harvestUnitsPerMint * batchHarvestsByMint.size);

    const computeLimitIx = ComputeBudgetProgram.setComputeUnitLimit({
      units: totalUnits,
    });
    const computePriceIx = ComputeBudgetProgram.setComputeUnitPrice({
      microLamports: 50000,
    });
    transaction.add(computeLimitIx);
    transaction.add(computePriceIx);

    // Add harvest instructions BEFORE close instructions (grouped by mint for efficiency)
    for (const [mintStr, sources] of batchHarvestsByMint) {
      const harvestIx = createHarvestWithheldTokensToMintInstruction(
        new PublicKey(mintStr),
        sources,
        TOKEN_2022_PROGRAM_ID
      );
      transaction.add(harvestIx);
      console.log(`[burn] Added harvest instruction for mint ${mintStr.slice(0, 8)}... with ${sources.length} source(s)`);
    }

    // Add close instructions for each account
    for (const account of accountsToClose) {
      const closeInstruction = createCloseAccountInstruction(
        account.pubkey,
        owner,
        owner,
        [],
        account.programId
      );
      transaction.add(closeInstruction);
    }

    // Calculate total rent and fee
    const totalRentLamports = accountsToClose.length * TYPICAL_RENT_LAMPORTS;
    const feeAmount = Math.floor(totalRentLamports * FORGEPAD_FEE_PERCENT / 100);

    // Add platform fee transfer
    if (feeAmount > 0) {
      const feeInstruction = SystemProgram.transfer({
        fromPubkey: owner,
        toPubkey: new PublicKey(FORGEPAD_FEE_WALLET),
        lamports: feeAmount,
      });
      transaction.add(feeInstruction);
      console.log(`[burn] Added batch platform fee: ${feeAmount} lamports (${FORGEPAD_FEE_PERCENT}% of ${totalRentLamports} rent)`);
    }

    // Get fresh blockhash
    const { blockhash } = await connection.getLatestBlockhash("confirmed");
    transaction.recentBlockhash = blockhash;
    transaction.feePayer = owner;

    // Pre-simulate transaction to catch errors before user signs
    const simulation = await connection.simulateTransaction(transaction);

    if (simulation.value.err) {
      console.error("[burn] Simulation failed:", simulation.value.err);
      console.error("[burn] Simulation logs:", simulation.value.logs);
      throw new Error(`Transaction simulation failed: ${JSON.stringify(simulation.value.err)}`);
    }

    console.log("[burn] Simulation passed, sending transaction");

    // Request signature
    onStatus?.({ stage: "signing", message: "Approve in your wallet..." });
    signature = await sendTransaction(transaction);

    console.log(`[burn] Batch close transaction sent:`, signature, `(${accountsToClose.length} accounts)`);

    // Wait for confirmation
    onStatus?.({
      stage: "confirming",
      message: "Confirming on-chain...",
      signature,
    });

    const result = await waitForConfirmation(connection, signature, 90000, (msg) =>
      onStatus?.({ stage: "confirming", message: msg, signature: signature! })
    );

    if (result.success) {
      const netReclaimed = totalRentLamports - feeAmount;
      onStatus?.({
        stage: "success",
        message: `Closed ${accountsToClose.length} accounts! Reclaimed ${(netReclaimed / 1e9).toFixed(6)} SOL`,
        signature,
      });
      return {
        success: true,
        signature,
        accountsClosed: accountsToClose.length,
        rentReclaimed: netReclaimed,
        feePaid: feeAmount,
      };
    } else {
      onStatus?.({
        stage: "error",
        message: result.status,
        signature,
        error: result.status,
      });
      return { success: false, signature, accountsClosed: 0, rentReclaimed: 0, feePaid: 0, error: result.status };
    }
  } catch (error) {
    console.error("[burn] Batch close error:", error);

    // Check if transaction might have succeeded despite error
    if (signature && mightHaveSucceeded(error)) {
      onStatus?.({ stage: "checking", message: "Verifying transaction status...", signature });
      await new Promise((resolve) => setTimeout(resolve, 3000));

      const status = await checkSignatureStatus(connection, signature);
      if (status === "confirmed" || status === "finalized") {
        const totalRentLamports = accountsToClose.length * TYPICAL_RENT_LAMPORTS;
        const feeAmount = Math.floor(totalRentLamports * FORGEPAD_FEE_PERCENT / 100);
        const netReclaimed = totalRentLamports - feeAmount;

        onStatus?.({
          stage: "success",
          message: `Closed ${accountsToClose.length} accounts! Reclaimed ${(netReclaimed / 1e9).toFixed(6)} SOL`,
          signature,
        });
        return {
          success: true,
          signature,
          accountsClosed: accountsToClose.length,
          rentReclaimed: netReclaimed,
          feePaid: feeAmount,
        };
      }
    }

    const friendlyError = getUserFriendlyError(error);
    onStatus?.({
      stage: "error",
      message: friendlyError,
      signature: signature || undefined,
      error: friendlyError,
    });

    return {
      success: false,
      signature: signature || undefined,
      accountsClosed: 0,
      rentReclaimed: 0,
      feePaid: 0,
      error: friendlyError,
    };
  }
}

/**
 * Result of burning NFTs
 */
export interface BurnNFTResult {
  success: boolean;
  signature?: string;
  nftsBurned: number;
  rentReclaimed: number; // in lamports (net after fee)
  feePaid: number; // in lamports
  error?: string;
  /** Details of any partial failures */
  failures?: Array<{ mint: string; error: string }>;
}

/**
 * Build a transaction instruction for burning a single NFT using Metaplex burnV1
 *
 * @param connection - Solana RPC connection
 * @param nft - NFT info object
 * @param owner - Owner wallet public key
 * @returns Transaction instruction for burning the NFT
 */
async function buildBurnNFTInstruction(
  connection: Connection,
  nft: NFTInfo,
  owner: PublicKey
): Promise<TransactionInstruction> {
  // Create a Umi instance using the RPC endpoint
  const umi = createUmi(connection.rpcEndpoint).use(mplTokenMetadata());

  // Create a noop signer for the owner (we'll sign the tx ourselves)
  const ownerSigner = createNoopSigner(umiPublicKey(owner.toBase58()));

  // Build the burnV1 instruction
  // This handles burning the token and closing all related accounts (metadata, edition)

  // Defensive: ensure we have proper base58 strings for Umi
  // nft.mint and nft.tokenAccount may be PublicKey objects or strings depending on source
  const mintStr = typeof nft.mint === 'string' ? nft.mint : nft.mint.toBase58();
  const tokenAccountStr = typeof nft.tokenAccount === 'string' ? nft.tokenAccount : nft.tokenAccount.toBase58();

  console.log('[burn-nft] Burning NFT:', { mint: mintStr, tokenAccount: tokenAccountStr, name: nft.name });

  // Build burnV1 config
  const burnConfig: any = {
    mint: umiPublicKey(mintStr),
    authority: ownerSigner,
    tokenOwner: umiPublicKey(owner.toBase58()),
    token: umiPublicKey(tokenAccountStr),
    tokenStandard: TokenStandard.NonFungible,
  };

  // If NFT belongs to a verified collection, pass collection metadata
  // Required by Metaplex burnV1 for collection NFTs (error 0x67 without it)
  if (nft.collectionAddress) {
    const collectionMintStr = typeof nft.collectionAddress === 'string'
      ? nft.collectionAddress
      : nft.collectionAddress.toBase58();
    burnConfig.collectionMetadata = findMetadataPda(umi, {
      mint: umiPublicKey(collectionMintStr),
    });
  }

  const burnIxBuilder = burnV1(umi, burnConfig);

  // Get the instructions from the builder
  const ixs = burnIxBuilder.getInstructions();

  if (ixs.length === 0) {
    throw new Error("Failed to build burn instruction");
  }

  // Convert Umi instruction to web3.js instruction
  return toWeb3JsInstruction(ixs[0]);
}

/**
 * Burn a batch of NFTs in a single transaction
 * Max 5 NFTs per transaction due to instruction size limits
 *
 * @param connection - Solana RPC connection
 * @param nfts - Array of NFTs to burn (max 5)
 * @param owner - Owner wallet public key
 * @param sendTransaction - Function to send the transaction
 * @param onStatus - Optional status callback
 * @returns Result with NFTs burned and rent reclaimed
 */
export async function burnNFTBatch(
  connection: Connection,
  nfts: NFTInfo[],
  owner: PublicKey,
  sendTransaction: (tx: Transaction) => Promise<string>,
  onStatus?: BurnStatusCallback
): Promise<BurnNFTResult> {
  if (nfts.length === 0) {
    return { success: true, nftsBurned: 0, rentReclaimed: 0, feePaid: 0 };
  }

  // Limit batch size
  const batch = nfts.slice(0, MAX_NFTS_PER_TX);

  let signature: string | null = null;
  const failures: Array<{ mint: string; error: string }> = [];
  const validNFTs: NFTInfo[] = [];

  try {
    onStatus?.({
      stage: "preparing",
      message: `Building transaction to burn ${batch.length} NFT${batch.length > 1 ? "s" : ""}...`,
    });

    const transaction = new Transaction();

    // Add compute budget instructions (NFT burns are heavier)
    const computeLimitIx = ComputeBudgetProgram.setComputeUnitLimit({
      units: 200000 * batch.length, // 200k units per NFT
    });
    const computePriceIx = ComputeBudgetProgram.setComputeUnitPrice({
      microLamports: 50000,
    });
    transaction.add(computeLimitIx);
    transaction.add(computePriceIx);

    // Validate ownership and build burn instructions for each NFT
    for (const nft of batch) {
      try {
        // Verify the NFT is still owned by the user
        const tokenAccountInfo = await getAccount(
          connection,
          nft.tokenAccount,
          "confirmed",
          TOKEN_PROGRAM_ID
        );

        if (tokenAccountInfo.amount !== 1n) {
          console.warn(
            `[burn-nft] NFT ${nft.mint.toBase58().slice(0, 8)}... has unexpected amount: ${tokenAccountInfo.amount}`
          );
          failures.push({
            mint: nft.mint.toBase58(),
            error: "NFT not found in wallet",
          });
          continue;
        }

        // Build the burn instruction
        const burnIx = await buildBurnNFTInstruction(connection, nft, owner);
        transaction.add(burnIx);
        validNFTs.push(nft);
      } catch (error) {
        console.error(
          `[burn-nft] Error preparing NFT ${nft.mint.toBase58().slice(0, 8)}...:`,
          error
        );
        failures.push({
          mint: nft.mint.toBase58(),
          error: error instanceof Error ? error.message : "Unknown error",
        });
      }
    }

    if (validNFTs.length === 0) {
      return {
        success: false,
        nftsBurned: 0,
        rentReclaimed: 0,
        feePaid: 0,
        error: "No valid NFTs to burn",
        failures,
      };
    }

    // Calculate fee based on expected rent reclaim
    const totalRentLamports = validNFTs.length * TYPICAL_NFT_RENT_LAMPORTS;
    const feeAmount = Math.floor((totalRentLamports * FORGEPAD_FEE_PERCENT) / 100);

    // Add platform fee transfer
    if (feeAmount > 0) {
      const feeInstruction = SystemProgram.transfer({
        fromPubkey: owner,
        toPubkey: new PublicKey(FORGEPAD_FEE_WALLET),
        lamports: feeAmount,
      });
      transaction.add(feeInstruction);
    }

    // Get fresh blockhash
    const { blockhash } = await connection.getLatestBlockhash("confirmed");
    transaction.recentBlockhash = blockhash;
    transaction.feePayer = owner;

    // Pre-simulate transaction to catch errors before user signs
    const simulation = await connection.simulateTransaction(transaction);

    if (simulation.value.err) {
      console.error("[burn-nft] Simulation failed:", simulation.value.err);
      console.error("[burn-nft] Simulation logs:", simulation.value.logs);
      throw new Error(`Transaction simulation failed: ${JSON.stringify(simulation.value.err)}`);
    }

    console.log("[burn-nft] Simulation passed, sending transaction");

    // Request signature
    onStatus?.({ stage: "signing", message: "Approve in your wallet..." });
    signature = await sendTransaction(transaction);

    console.log(
      `[burn-nft] Transaction sent:`,
      signature,
      `(${validNFTs.length} NFTs)`
    );

    // Wait for confirmation
    onStatus?.({
      stage: "confirming",
      message: "Confirming on-chain...",
      signature,
    });

    const result = await waitForConfirmation(connection, signature, 90000, (msg) =>
      onStatus?.({ stage: "confirming", message: msg, signature: signature! })
    );

    if (result.success) {
      const netReclaimed = totalRentLamports - feeAmount;
      const successMsg = `Burned ${validNFTs.length} NFT${validNFTs.length > 1 ? "s" : ""}! Reclaimed ${(netReclaimed / 1e9).toFixed(4)} SOL`;

      onStatus?.({
        stage: "success",
        message: successMsg,
        signature,
      });

      return {
        success: true,
        signature,
        nftsBurned: validNFTs.length,
        rentReclaimed: netReclaimed,
        feePaid: feeAmount,
        failures: failures.length > 0 ? failures : undefined,
      };
    } else {
      onStatus?.({
        stage: "error",
        message: result.status,
        signature,
        error: result.status,
      });
      return {
        success: false,
        signature,
        nftsBurned: 0,
        rentReclaimed: 0,
        feePaid: 0,
        error: result.status,
        failures,
      };
    }
  } catch (error) {
    console.error("[burn-nft] Batch burn error:", error);

    // Check if transaction might have succeeded despite error
    if (signature && mightHaveSucceeded(error)) {
      onStatus?.({
        stage: "checking",
        message: "Verifying transaction status...",
        signature,
      });
      await new Promise((resolve) => setTimeout(resolve, 3000));

      const status = await checkSignatureStatus(connection, signature);
      if (status === "confirmed" || status === "finalized") {
        const totalRentLamports = validNFTs.length * TYPICAL_NFT_RENT_LAMPORTS;
        const feeAmount = Math.floor((totalRentLamports * FORGEPAD_FEE_PERCENT) / 100);
        const netReclaimed = totalRentLamports - feeAmount;

        onStatus?.({
          stage: "success",
          message: `Burned ${validNFTs.length} NFT${validNFTs.length > 1 ? "s" : ""}! Reclaimed ${(netReclaimed / 1e9).toFixed(4)} SOL`,
          signature,
        });
        return {
          success: true,
          signature,
          nftsBurned: validNFTs.length,
          rentReclaimed: netReclaimed,
          feePaid: feeAmount,
          failures: failures.length > 0 ? failures : undefined,
        };
      }
    }

    const friendlyError = getUserFriendlyError(error);
    onStatus?.({
      stage: "error",
      message: friendlyError,
      signature: signature || undefined,
      error: friendlyError,
    });

    return {
      success: false,
      signature: signature || undefined,
      nftsBurned: 0,
      rentReclaimed: 0,
      feePaid: 0,
      error: friendlyError,
      failures,
    };
  }
}

/**
 * Burn multiple NFTs, processing in batches
 * Handles large collections by splitting into multiple transactions
 *
 * @param connection - Solana RPC connection
 * @param nfts - Array of NFTs to burn
 * @param owner - Owner wallet public key
 * @param sendTransaction - Function to send the transaction
 * @param onStatus - Optional status callback
 * @returns Aggregated result from all batches
 */
export async function burnNFTs(
  connection: Connection,
  nfts: NFTInfo[],
  owner: PublicKey,
  sendTransaction: (tx: Transaction) => Promise<string>,
  onStatus?: BurnStatusCallback
): Promise<BurnNFTResult> {
  if (nfts.length === 0) {
    return { success: true, nftsBurned: 0, rentReclaimed: 0, feePaid: 0 };
  }

  let totalBurned = 0;
  let totalRentReclaimed = 0;
  let totalFeePaid = 0;
  const allFailures: Array<{ mint: string; error: string }> = [];
  let lastSignature: string | undefined;
  let lastError: string | undefined;

  // Process in batches of MAX_NFTS_PER_TX
  const batches: NFTInfo[][] = [];
  for (let i = 0; i < nfts.length; i += MAX_NFTS_PER_TX) {
    batches.push(nfts.slice(i, i + MAX_NFTS_PER_TX));
  }

  for (let i = 0; i < batches.length; i++) {
    const batch = batches[i];
    const batchNum = i + 1;
    const totalBatches = batches.length;

    onStatus?.({
      stage: "preparing",
      message:
        totalBatches > 1
          ? `Processing batch ${batchNum}/${totalBatches} (${batch.length} NFTs)...`
          : `Burning ${batch.length} NFT${batch.length > 1 ? "s" : ""}...`,
    });

    const result = await burnNFTBatch(
      connection,
      batch,
      owner,
      sendTransaction,
      onStatus
    );

    if (result.signature) {
      lastSignature = result.signature;
    }

    if (result.success) {
      totalBurned += result.nftsBurned;
      totalRentReclaimed += result.rentReclaimed;
      totalFeePaid += result.feePaid;
    } else {
      lastError = result.error;
    }

    if (result.failures) {
      allFailures.push(...result.failures);
    }

    // If a batch fails entirely, stop processing (user may have rejected)
    if (!result.success && result.nftsBurned === 0) {
      break;
    }
  }

  const overallSuccess = totalBurned > 0;

  if (overallSuccess) {
    onStatus?.({
      stage: "success",
      message: `Burned ${totalBurned} NFT${totalBurned > 1 ? "s" : ""}! Reclaimed ${(totalRentReclaimed / 1e9).toFixed(4)} SOL`,
      signature: lastSignature,
    });
  }

  return {
    success: overallSuccess,
    signature: lastSignature,
    nftsBurned: totalBurned,
    rentReclaimed: totalRentReclaimed,
    feePaid: totalFeePaid,
    error: overallSuccess ? undefined : lastError,
    failures: allFailures.length > 0 ? allFailures : undefined,
  };
}
